import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { alpha } from '@mui/material/styles';
import { memo } from 'react';
import { useFormContext } from '../../contexts/FormContext';
import { useFieldValue } from '../../hooks/useFormValues';
import type { FieldComponentProps } from '../form-engine/types';

const RadioField = ({ field }: FieldComponentProps) => {
  const { handleChange } = useFormContext();
  const selectedValue = useFieldValue(field.name!);
  const labelId = `${field.name}-label`;

  const setValue = (value: string | null) => handleChange({ target: { name: field.name!, value, type: 'radio' } }, field);

  return (
    <Grid size={field.size || 12} key={field.name} sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {field.number ? (
          <Typography
            variant="body2"
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              color: selectedValue ? 'primary.contrastText' : 'text.secondary',
              bgcolor: selectedValue ? 'primary.main' : 'action.selected',
            }}
          >
            {field.number as string | number}
          </Typography>
        ) : null}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography id={labelId} variant="subtitle1" color="text.primary" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                {field.label}
              </Typography>
              {field.note ? (
                <Typography variant="body2" color="text.secondary">
                  {field.note as string}
                </Typography>
              ) : null}
            </Box>
            <Button
              size="small"
              onClick={() => setValue(null)}
              sx={{ flexShrink: 0, my: -0.5, textTransform: 'none', visibility: selectedValue ? 'visible' : 'hidden' }}
            >
              Clear
            </Button>
          </Box>
          <RadioGroup value={selectedValue ?? ''} onChange={(e) => setValue(e.target.value)} aria-labelledby={labelId}>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {field.options!.map((option) => {
                const selected = option.value === selectedValue;
                return (
                  <Grid key={option.value} size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                      slotProps={{ typography: { variant: 'body2' } }}
                      sx={(theme) => ({
                        m: 0,
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        pr: 1.5,
                        py: 0.5,
                        border: 1,
                        borderRadius: 1,
                        color: 'text.primary',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        transition: theme.transitions.create(['border-color', 'background-color']),
                        '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, selected ? 0.12 : 0.04) },
                      })}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>
        </Box>
      </Box>
    </Grid>
  );
};

export default memo(RadioField);
